import { useContext, createContext, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";

import { getRequest, postRequest } from "../api/axiosRequests";
import setAuthToken from "../api/setAuthToken";

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const VALID_URS = [
    "/login",
    "/register",
    "/home",
    "/PrivacyPolicy",
    "/TermsOfService",
  ];
  const DEFAULT_USER = {
    id: -1,
    firstname: "",
    lastname: "",
    email: "",
    password: "",
  };
  const [user, setUser] = useState(DEFAULT_USER);
  const [role, setRole] = useState("None");
  const [userSubscription, setUserSubscription] = useState({ id: -1 });
  const [token, setToken] = useState(localStorage.getItem("jwt") || "");
  const navigate = useNavigate();
  const location = useLocation();

  const loginAction = async (data) => {
    try {
      const csrftoken = Cookies.get("csrftoken");
      const loginHeaders = { "X-CSRFToken": csrftoken };
      const response = await postRequest("login/", data, loginHeaders);
      const token = response.data["jwt"];
      const role = response.data["role"];
      const user = response.data["user"];

      const headers = { Authorization: token };
      const promiseSubscription = getRequest(
        "currentUserSubscription/" + user.id,
        headers
      );
      promiseSubscription
        .then((res) => {
          const userSubscription = res.data;
          // Set token to local storage
          localStorage.setItem("jwt", token);

          // Set token to axios common header
          setAuthToken(token);

          setToken(token);
          setUser(user);
          setRole(role.name);
          setUserSubscription(userSubscription);

          // Navigate to profile page
          navigate("/account/Profile");
        })
        .catch((errs) => {
          console.log(errs);
          if (errs.response) {
            // In case of errors redirect to home
            alert("An error occured, please try again!");
            navigate("/home");
          }
        });
    } catch (err) {
      console.error(err);
      const data = err.response.data;
      let alertMessage = "Error logging in";
      if (data && data["error"]) {
        alertMessage = data["error"];
      }
      alert(alertMessage);
    }
  };

  const logOut = () => {
    setUser(DEFAULT_USER);
    setToken("");
    setRole("None");
    localStorage.removeItem("completedExercises");
    localStorage.removeItem("jwt");
    navigate("/");
  };

  const refresh = () => {
    // Check if token is in local storage, if not redirect to home
    const tok = localStorage.getItem("jwt");
    if (tok === "" || tok === undefined || tok === null) {
      // Stay in the page if it is a valid url
      if (location.pathname && VALID_URS.indexOf(location.pathname) !== -1) {
        return;
      }
      navigate("/home");
      return;
    }

    // Get user data and set it to the state
    const headers = { Authorization: tok };
    const promiseUser = getRequest("userData/", headers);
    promiseUser
      .then((res) => {
        const user = res.data["data"];
        const role = res.data["role"];

        setUser(user);
        setRole(role);
        setToken(tok);

        const promiseSubscription = getRequest(
          "currentUserSubscription/" + user.id,
          headers
        );
        promiseSubscription
          .then((res) => {
            const userSubscription = res.data;
            setUserSubscription(userSubscription);
          })
          .catch((errs) => {
            console.log(errs);
            if (errs.response) {
              // In case of errors redirect to home
              alert("An error occured, please try again!");
              navigate("/home");
            }
          });
      })
      .catch((errs) => {
        console.log(errs);
        if (errs.response) {
          // In case of errors redirect to home
          alert("An error occured, please try again!");
          navigate("/home");
        }
      });
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        role,
        userSubscription,
        loginAction,
        logOut,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

export const useAuth = () => {
  return useContext(AuthContext);
};
